/**
 * Per-body-type trip / idle boundary defaults from fleetconsole
 * (`BODY_BOUNDARY`, `CLASS_BOUNDARY`, `STOP_CLOSE_DEFAULTS`, idle policy defaults).
 *
 * Selecting a vehicle type in the profiler reloads these limits the same way
 * changing body type does in the console.
 */

import {
    ENABLE_OPTION,
    DISABLE_OPTION,
    EXCUSABLE_IDLING_ENABLED_FIELD_KEY,
    defaultExcusableIdlingEnabled,
    AMBIENT_PROXY_FIELD_KEY,
    IGNITION_SOURCE_FIELD_KEY,
    IGN_RPM_MIN_FIELD_KEY,
    IGN_DC_VOLT_ON_FIELD_KEY,
    STOP_CLOSE_ENABLED_FIELD_KEY,
    STOP_LENGTH_TO_CLOSE_FIELD_KEY,
    STOP_SPEED_FIELD_KEY,
    STOP_DRIFT_ALLOWANCE_FIELD_KEY,
} from './vehiclePolicyConfig';
import { normalizeVehicleClassKey } from './vehicleTypeOptions';

/** @typedef {{
 *   movingSpeedThresholdKmh: number,
 *   excessiveIdleLimitSec: number,
 *   tripStartSpeedKmh: number,
 *   tripIgnOffTimeoutSec: number,
 *   tripStartDistThresholdKm: number,
 *   tripMinDistanceKm: number,
 *   accel?: number,
 *   excusableIdlingMaxSec: number | null,
 * }} BodyBoundaryPreset */

/** Per-body-type boundary + idle limits (fleetconsole `BODY_BOUNDARY`). */
const BODY_BOUNDARY = {
    MOTORCYCLE: {
        movingSpeedThresholdKmh: 3,
        excessiveIdleLimitSec: 180,
        tripStartSpeedKmh: 4,
        tripIgnOffTimeoutSec: 45,
        tripStartDistThresholdKm: 0.02,
        tripMinDistanceKm: 0.2,
        accel: 0.10,
        excusableIdlingMaxSec: null,
    },
    HATCHBACK: {
        movingSpeedThresholdKmh: 5,
        excessiveIdleLimitSec: 240,
        tripStartSpeedKmh: 5,
        tripIgnOffTimeoutSec: 60,
        tripStartDistThresholdKm: 0.04,
        tripMinDistanceKm: 0.4,
        accel: 0.10,
        excusableIdlingMaxSec: 1800,
    },
    SEDAN: {
        movingSpeedThresholdKmh: 5,
        excessiveIdleLimitSec: 300,
        tripStartSpeedKmh: 5,
        tripIgnOffTimeoutSec: 60,
        tripStartDistThresholdKm: 0.05,
        tripMinDistanceKm: 0.5,
        accel: 0.10,
        excusableIdlingMaxSec: 1800,
    },
    SUV: {
        movingSpeedThresholdKmh: 5,
        excessiveIdleLimitSec: 300,
        tripStartSpeedKmh: 5,
        tripIgnOffTimeoutSec: 65,
        tripStartDistThresholdKm: 0.06,
        tripMinDistanceKm: 0.5,
        accel: 0.12,
        excusableIdlingMaxSec: 1800,
    },
    MPV_VAN: {
        movingSpeedThresholdKmh: 5,
        excessiveIdleLimitSec: 360,
        tripStartSpeedKmh: 5,
        tripIgnOffTimeoutSec: 70,
        tripStartDistThresholdKm: 0.06,
        tripMinDistanceKm: 0.5,
        accel: 0.12,
        excusableIdlingMaxSec: 2400,
    },
    PICKUP_4X4: {
        movingSpeedThresholdKmh: 5,
        excessiveIdleLimitSec: 270,
        tripStartSpeedKmh: 6,
        tripIgnOffTimeoutSec: 65,
        tripStartDistThresholdKm: 0.07,
        tripMinDistanceKm: 0.6,
        accel: 0.15,
        excusableIdlingMaxSec: 1800,
    },
    PANEL_VAN: {
        movingSpeedThresholdKmh: 5,
        excessiveIdleLimitSec: 300,
        tripStartSpeedKmh: 6,
        tripIgnOffTimeoutSec: 70,
        tripStartDistThresholdKm: 0.08,
        tripMinDistanceKm: 0.6,
        accel: 0.15,
        excusableIdlingMaxSec: 1800,
    },
    LIGHT_TRUCK: {
        movingSpeedThresholdKmh: 5,
        excessiveIdleLimitSec: 420,
        tripStartSpeedKmh: 6,
        tripIgnOffTimeoutSec: 75,
        tripStartDistThresholdKm: 0.10,
        tripMinDistanceKm: 0.8,
        accel: 0.15,
        excusableIdlingMaxSec: 2400,
    },
    MINIBUS: {
        movingSpeedThresholdKmh: 5,
        excessiveIdleLimitSec: 600,
        tripStartSpeedKmh: 5,
        tripIgnOffTimeoutSec: 75,
        tripStartDistThresholdKm: 0.08,
        tripMinDistanceKm: 0.5,
        accel: 0.15,
        excusableIdlingMaxSec: 3600,
    },
    MEDIUM_TRUCK: {
        movingSpeedThresholdKmh: 6,
        excessiveIdleLimitSec: 600,
        tripStartSpeedKmh: 6,
        tripIgnOffTimeoutSec: 90,
        tripStartDistThresholdKm: 0.12,
        tripMinDistanceKm: 1.0,
        accel: 0.18,
        excusableIdlingMaxSec: 3600,
    },
    HEAVY_TRUCK: {
        movingSpeedThresholdKmh: 6,
        excessiveIdleLimitSec: 600,
        tripStartSpeedKmh: 7,
        tripIgnOffTimeoutSec: 90,
        tripStartDistThresholdKm: 0.15,
        tripMinDistanceKm: 1.0,
        accel: 0.20,
        excusableIdlingMaxSec: 3600,
    },
    PRIME_MOVER: {
        movingSpeedThresholdKmh: 6,
        excessiveIdleLimitSec: 900,
        tripStartSpeedKmh: 7,
        tripIgnOffTimeoutSec: 120,
        tripStartDistThresholdKm: 0.20,
        tripMinDistanceKm: 1.5,
        accel: 0.20,
        excusableIdlingMaxSec: 5400,
    },
    BUS_CITY: {
        movingSpeedThresholdKmh: 5,
        excessiveIdleLimitSec: 900,
        tripStartSpeedKmh: 5,
        tripIgnOffTimeoutSec: 90,
        tripStartDistThresholdKm: 0.08,
        tripMinDistanceKm: 0.5,
        accel: 0.20,
        excusableIdlingMaxSec: 5400,
    },
    BUS_COACH: {
        movingSpeedThresholdKmh: 6,
        excessiveIdleLimitSec: 720,
        tripStartSpeedKmh: 6,
        tripIgnOffTimeoutSec: 100,
        tripStartDistThresholdKm: 0.10,
        tripMinDistanceKm: 1.2,
        accel: 0.20,
        excusableIdlingMaxSec: 3600,
    },
};

/** Horizon vehicle-class fallback rows (fleetconsole `CLASS_BOUNDARY`). */
const CLASS_BOUNDARY = {
    BIKE: {
        movingSpeedThresholdKmh: 3,
        excessiveIdleLimitSec: 180,
        tripStartSpeedKmh: 5,
        tripIgnOffTimeoutSec: 60,
        tripStartDistThresholdKm: 0.05,
        tripMinDistanceKm: 0.5,
        accel: 0.10,
        excusableIdlingMaxSec: null,
    },
    CAR_SUV: {
        movingSpeedThresholdKmh: 5,
        excessiveIdleLimitSec: 300,
        tripStartSpeedKmh: 5,
        tripIgnOffTimeoutSec: 60,
        tripStartDistThresholdKm: 0.05,
        tripMinDistanceKm: 0.5,
        accel: 0.10,
        excusableIdlingMaxSec: 1800,
    },
    VAN_LTRUCK: {
        movingSpeedThresholdKmh: 5,
        excessiveIdleLimitSec: 300,
        tripStartSpeedKmh: 5,
        tripIgnOffTimeoutSec: 60,
        tripStartDistThresholdKm: 0.05,
        tripMinDistanceKm: 0.5,
        accel: 0.15,
        excusableIdlingMaxSec: 1800,
    },
    HEAVY_TRUCK: {
        movingSpeedThresholdKmh: 5,
        excessiveIdleLimitSec: 600,
        tripStartSpeedKmh: 5,
        tripIgnOffTimeoutSec: 60,
        tripStartDistThresholdKm: 0.05,
        tripMinDistanceKm: 0.5,
        accel: 0.20,
        excusableIdlingMaxSec: 3600,
    },
    BUS: {
        movingSpeedThresholdKmh: 5,
        excessiveIdleLimitSec: 900,
        tripStartSpeedKmh: 5,
        tripIgnOffTimeoutSec: 90,
        tripStartDistThresholdKm: 0.05,
        tripMinDistanceKm: 0.5,
        accel: 0.20,
        excusableIdlingMaxSec: 5400,
    },
};

const BODY_TYPE_META = [
    { key: 'MOTORCYCLE', label: 'Motorcycle', vclass: 'BIKE' },
    { key: 'HATCHBACK', label: 'Hatchback', vclass: 'CAR_SUV' },
    { key: 'SEDAN', label: 'Sedan', vclass: 'CAR_SUV' },
    { key: 'SUV', label: 'SUV', vclass: 'CAR_SUV' },
    { key: 'MPV_VAN', label: 'MPV / people carrier', vclass: 'CAR_SUV' },
    { key: 'PICKUP_4X4', label: 'Pickup / 4x4', vclass: 'VAN_LTRUCK' },
    { key: 'PANEL_VAN', label: 'Panel van', vclass: 'VAN_LTRUCK' },
    { key: 'LIGHT_TRUCK', label: 'Light duty truck (<=7.5 t)', vclass: 'VAN_LTRUCK' },
    { key: 'MINIBUS', label: 'Minibus (<=20 seats)', vclass: 'BUS' },
    { key: 'MEDIUM_TRUCK', label: 'Medium duty truck (7.5-16 t)', vclass: 'HEAVY_TRUCK' },
    { key: 'HEAVY_TRUCK', label: 'Heavy duty truck (>16 t rigid)', vclass: 'HEAVY_TRUCK' },
    { key: 'PRIME_MOVER', label: 'Prime mover / articulated', vclass: 'HEAVY_TRUCK' },
    { key: 'BUS_CITY', label: 'Commercial bus - city / stage', vclass: 'BUS' },
    { key: 'BUS_COACH', label: 'Commercial bus - coach / express', vclass: 'BUS' },
];

/** Shared idle-policy defaults applied with every body type (fleetconsole `FUEL_DEF`). */
const IDLE_POLICY_DEFAULTS = {
    excusableTempHighC: 35,
    excusableTempLowC: 5,
    excusableProxy: 'OFF',
};

const STOP_CLOSE_DEFAULTS = {
    stopCloseEnabled: false,
    tripCloseOnStopSec: 1200,
    tripStopSpeedKmh: 3,
    tripStopDriftM: 50,
    ignOverride: 'AUTO',
    ignRpmMin: 0,
    ignDcVoltOnV: 28,
};

const normalizeTypeToken = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_/]+/g, ' ')
    .replace(/[^a-z0-9.\s-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Extra aliases for Horizon free-text vehicle types / fallback names. */
const BODY_TYPE_ALIASES = {
    motorcycle: 'MOTORCYCLE',
    bike: 'MOTORCYCLE',
    hatchback: 'HATCHBACK',
    hatch: 'HATCHBACK',
    sedan: 'SEDAN',
    suv: 'SUV',
    mpv: 'MPV_VAN',
    'mpv van': 'MPV_VAN',
    'people carrier': 'MPV_VAN',
    pickup: 'PICKUP_4X4',
    '4x4': 'PICKUP_4X4',
    'pickup 4x4': 'PICKUP_4X4',
    'panel van': 'PANEL_VAN',
    van: 'PANEL_VAN',
    'light duty truck': 'LIGHT_TRUCK',
    'light truck': 'LIGHT_TRUCK',
    minibus: 'MINIBUS',
    'medium duty truck': 'MEDIUM_TRUCK',
    'medium truck': 'MEDIUM_TRUCK',
    'heavy duty truck': 'HEAVY_TRUCK',
    'heavy truck': 'HEAVY_TRUCK',
    'prime mover': 'PRIME_MOVER',
    articulated: 'PRIME_MOVER',
    'commercial bus': 'BUS_CITY',
    bus: 'BUS_CITY',
    'city bus': 'BUS_CITY',
    coach: 'BUS_COACH',
    'bus coach': 'BUS_COACH',
};

const stopCloseTimeoutForClass = (vclass) => {
    if (vclass === 'HEAVY_TRUCK' || vclass === 'BUS') return 1200;
    if (vclass === 'VAN_LTRUCK') return 900;
    return STOP_CLOSE_DEFAULTS.tripCloseOnStopSec;
};

/**
 * Resolve a Horizon vehicle-type string to a body-type key (e.g. MEDIUM_TRUCK).
 * Exact key / alias / label only — no substring matching (short aliases like "van"
 * would silently load PANEL_VAN limits for any custom name that contains them).
 * @param {string} vehicleType
 * @returns {string | null}
 */
export const resolveBodyTypeKey = (vehicleType) => {
    const raw = String(vehicleType || '').trim();
    if (!raw) return null;

    const asKey = raw.toUpperCase().replace(/[\s/-]+/g, '_');
    if (BODY_BOUNDARY[asKey]) return asKey;

    const normalized = normalizeTypeToken(raw);
    if (BODY_TYPE_ALIASES[normalized]) return BODY_TYPE_ALIASES[normalized];

    const byLabel = BODY_TYPE_META.find(
        (row) => normalizeTypeToken(row.label) === normalized,
    );
    if (byLabel) return byLabel.key;

    return null;
};

/**
 * Build the merged boundary preset for a vehicle type (body row over class row).
 * Body type is exact-match only. Class fallback requires an explicit vehicleClass hint
 * from GET /v3/vehicleTypes — names are not fuzzy-matched.
 * @param {string} vehicleType
 * @param {{ vehicleClass?: string }} [hints] - class from admin vehicleTypes when available
 * @returns {{ bodyKey: string | null, vclass: string | null, boundary: object } | null}
 */
export const resolveBodyTypeBoundaryPreset = (vehicleType, hints = {}) => {
    const bodyKey = resolveBodyTypeKey(vehicleType);
    const hintedClass = normalizeVehicleClassKey(hints.vehicleClass);
    let vclass = null;
    if (bodyKey) {
        vclass = BODY_TYPE_META.find((row) => row.key === bodyKey)?.vclass || null;
    } else if (CLASS_BOUNDARY[hintedClass]) {
        vclass = hintedClass;
    }

    if (!bodyKey && !vclass) return null;

    const classRow = (vclass && CLASS_BOUNDARY[vclass]) || CLASS_BOUNDARY.CAR_SUV;
    const bodyRow = (bodyKey && BODY_BOUNDARY[bodyKey]) || {};
    const boundary = {
        ...STOP_CLOSE_DEFAULTS,
        ...classRow,
        ...bodyRow,
        tripCloseOnStopSec: stopCloseTimeoutForClass(vclass || 'CAR_SUV'),
        ...IDLE_POLICY_DEFAULTS,
    };
    // Class/type-derived default, applied only when the body row does not state its own value —
    // same "body row wins" contract as the excusable cap below. Deliberately not a key on
    // IDLE_POLICY_DEFAULTS: that object is spread last, so a value there would be unreadable.
    if (!Object.prototype.hasOwnProperty.call(bodyRow, 'excusableIdlingEnabled')) {
        boundary.excusableIdlingEnabled = defaultExcusableIdlingEnabled(
            vehicleType,
            vclass || hintedClass,
        );
    }
    // Body-specific excusable cap (including explicit null) wins over class / idle defaults.
    if (Object.prototype.hasOwnProperty.call(bodyRow, 'excusableIdlingMaxSec')) {
        boundary.excusableIdlingMaxSec = bodyRow.excusableIdlingMaxSec;
    }

    return { bodyKey, vclass, boundary };
};

const toFormNumber = (value) => (
    value === null || value === undefined || value === '' ? '' : String(value)
);

/**
 * Map a vehicle type to profiler field values for Motion & Idling + Trip Boundaries.
 * Prefers body-type BODY_BOUNDARY match; otherwise uses vehicleClass / CLASS_BOUNDARY.
 * @param {string} vehicleType
 * @param {{ vehicleClass?: string }} [hints]
 * @returns {Record<string, string|number|boolean>|null}
 */
export const getTripIdleFieldValuesForVehicleType = (vehicleType, hints = {}) => {
    const resolved = resolveBodyTypeBoundaryPreset(vehicleType, hints);
    if (!resolved) return null;

    const { boundary } = resolved;
    return {
        'Excessive Idling/Standby Timeout (Sec)': toFormNumber(boundary.excessiveIdleLimitSec),
        'Minimum Speed to Register Movement (km/h)': toFormNumber(boundary.movingSpeedThresholdKmh),
        [EXCUSABLE_IDLING_ENABLED_FIELD_KEY]: boundary.excusableIdlingEnabled
            ? ENABLE_OPTION
            : DISABLE_OPTION,
        'Max Excusable Idle Duration (Sec)': toFormNumber(boundary.excusableIdlingMaxSec),
        'Hot Threshold (°C)': toFormNumber(boundary.excusableTempHighC),
        'Cold Threshold (°C)': toFormNumber(boundary.excusableTempLowC),
        [AMBIENT_PROXY_FIELD_KEY]: boundary.excusableProxy || 'OFF',
        'Software Trip Start Speed (km/h)': toFormNumber(boundary.tripStartSpeedKmh),
        'Minimum Distance to Force Trip Start (km)': toFormNumber(boundary.tripStartDistThresholdKm),
        'Ignition OFF Trip End Timeout (Sec)': toFormNumber(boundary.tripIgnOffTimeoutSec),
        'Minimum Total Distance for Valid Trip (km)': toFormNumber(boundary.tripMinDistanceKm),
        [IGNITION_SOURCE_FIELD_KEY]: boundary.ignOverride || 'AUTO',
        [IGN_RPM_MIN_FIELD_KEY]: toFormNumber(boundary.ignRpmMin),
        [IGN_DC_VOLT_ON_FIELD_KEY]: toFormNumber(boundary.ignDcVoltOnV),
        [STOP_CLOSE_ENABLED_FIELD_KEY]: boundary.stopCloseEnabled ? ENABLE_OPTION : DISABLE_OPTION,
        [STOP_LENGTH_TO_CLOSE_FIELD_KEY]: toFormNumber(boundary.tripCloseOnStopSec),
        [STOP_SPEED_FIELD_KEY]: toFormNumber(boundary.tripStopSpeedKmh),
        [STOP_DRIFT_ALLOWANCE_FIELD_KEY]: toFormNumber(boundary.tripStopDriftM),
    };
};

/**
 * True when Motion/Trip fields differ from the defaults for `vehicleType`
 * (user-tuned, or filled without a matching preset).
 * @param {Record<string, unknown>} fieldValues
 * @param {string} vehicleType
 * @param {{ vehicleClass?: string }} [hints]
 * @returns {boolean}
 */
export const tripIdleFieldsDifferFromDefaults = (fieldValues, vehicleType, hints = {}) => {
    const defaults = getTripIdleFieldValuesForVehicleType(vehicleType, hints);
    if (defaults) {
        return Object.entries(defaults).some(
            ([key, value]) => String(fieldValues?.[key] ?? '') !== String(value ?? ''),
        );
    }
    const sample = getTripIdleFieldValuesForVehicleType('SUV', { vehicleClass: 'CAR_SUV' });
    return Object.keys(sample || {}).some((key) => {
        const current = fieldValues?.[key];
        return current !== '' && current != null;
    });
};
